import { useMemo } from "react";
import { itemImage, money } from "@/lib/api";
import { FAMILIES, familyOf } from "@/lib/families";
import { byWearScore } from "@/lib/wear";
import { ArrowUpRight, Tag } from "lucide-react";

/**
 * Closet landing - "The Edit". A luxury-retail bento of section tiles (Everything, Shirts, Sweaters, Jackets, Pants ...),
 * each a composed mosaic of that section's most-worn studio renders on white, with the numbers that matter underneath.
 * Tap a tile to open the section. No skeuomorphism - the renders are the hero.
 */
const itemValue = (i) => (i.worth_override ?? i.estimated_worth ?? null);

const pickPhotos = (list, n) => {
  const sorted = list.slice().sort(byWearScore);
  const good = sorted.filter((i) => i.has_enhanced && !i.review?.needs_front_photo);
  const rest = sorted.filter((i) => !good.includes(i));
  return [...good, ...rest].slice(0, n);
};

const Cell = ({ item, big = false }) => (
  <div className={`relative bg-white overflow-hidden ${big ? "row-span-2" : ""}`}>
    {item ? (
      <img src={itemImage(item)} alt="" loading="lazy" draggable={false} className="absolute inset-0 h-full w-full object-contain p-[9%] transition-transform duration-500 ease-out group-hover:scale-[1.035]" />
    ) : (
      <div className="absolute inset-0 bg-gray-25" />
    )}
  </div>
);

const Tile = ({ s, span, onOpen }) => {
  const photos = pickPhotos(s.items, span === "wide" ? 6 : 3);
  const top = s.items.slice().sort(byWearScore)[0];
  const value = s.items.reduce((sum, i) => sum + (Number(itemValue(i)) || 0), 0);
  const wears = s.items.reduce((sum, i) => sum + (Number(i.wears_60d) || 0), 0);
  const Icon = s.icon;
  return (
    <button
      type="button"
      onClick={() => onOpen(s.key)}
      className={`group relative text-left rounded-[var(--radius)] border border-border bg-card overflow-hidden lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
        span === "wide" ? "md:col-span-2 xl:col-span-2 xl:row-span-2" : ""
      }`}
      data-testid={`closet-section-${s.key}`}
      aria-label={`Open ${s.label}, ${s.items.length} pieces`}
    >
      {/* mosaic */}
      <div className={`grid gap-px bg-border ${span === "wide" ? "grid-cols-3 grid-rows-2 aspect-[3/2] xl:aspect-auto xl:h-[calc(100%-92px)]" : photos.length >= 3 ? "grid-cols-[1.35fr_1fr] grid-rows-2 aspect-[4/3]" : photos.length === 2 ? "grid-cols-2 aspect-[4/3]" : "grid-cols-1 aspect-[4/3]"}`}>
        {span === "wide"
          ? [0, 1, 2, 3, 4, 5].map((k) => <Cell key={k} item={photos[k]} />)
          : photos.length >= 3
            ? [<Cell key="a" item={photos[0]} big />, <Cell key="b" item={photos[1]} />, <Cell key="c" item={photos[2]} />]
            : photos.map((p) => <Cell key={p.id} item={p} />)}
      </div>
      {/* facts */}
      <div className="h-[92px] px-5 py-4 flex items-end justify-between gap-4 border-t border-border bg-card">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground" data-testid={`closet-section-count-${s.key}`}>{s.items.length} piece{s.items.length === 1 ? "" : "s"}</p>
          <h3 className="font-display text-[26px] leading-none mt-1.5 flex items-center gap-2" data-testid={`closet-section-label-${s.key}`}>{Icon && <Icon className="h-[18px] w-[18px]" />}{s.label}</h3>
        </div>
        <div className="text-right shrink-0">
          {s.key !== "accessories" && value > 0 && <p className="font-mono-num font-semibold text-sm tabular-nums" data-testid={`closet-section-value-${s.key}`}>{money(Math.round(value))}</p>}
          <p className="text-[11px] text-muted-foreground mt-0.5 truncate max-w-[160px]">{wears > 0 ? `${wears} wear${wears === 1 ? "" : "s"} · 60 days` : top ? `Most worn · ${top.name || top.subtype}` : ""}</p>
        </div>
        <ArrowUpRight className="absolute right-4 top-4 h-4 w-4 text-white mix-blend-difference opacity-0 -translate-y-1 translate-x-1 transition-[opacity,transform] duration-200 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0" aria-hidden="true" />
      </div>
    </button>
  );
};

export const ClosetLanding = ({ items, onOpen, loading = false }) => {
  const sections = useMemo(() => {
    const live = (items || []).filter((i) => i.listing_type !== "sold");
    const out = [{ key: "all", label: "Everything", items: live }];
    for (const f of FAMILIES) {
      const list = live.filter((i) => familyOf(i) === f.key);
      if (list.length) out.push({ key: f.key, label: f.label, items: list });
    }
    const listed = live.filter((i) => ["sell", "swap", "both"].includes(i.listing_type));
    if (listed.length) out.push({ key: "forsale", label: "For sale", items: listed, icon: Tag });
    const sold = (items || []).filter((i) => i.listing_type === "sold");
    if (sold.length) out.push({ key: "sold", label: "Sold", items: sold });
    return out;
  }, [items]);

  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="closet-landing" aria-busy="true">
        <div className="md:col-span-2 xl:row-span-2 rounded-[var(--radius)] border border-border bg-card animate-pulse min-h-[420px]" />
        {[0, 1, 2, 3].map((i) => <div key={i} className="rounded-[var(--radius)] border border-border bg-card animate-pulse aspect-[4/3]" />)}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="closet-landing">
      {sections.map((s, idx) => <Tile key={s.key} s={s} span={idx === 0 ? "wide" : "std"} onOpen={onOpen} />)}
    </div>
  );
};
